import {
  BOOKING_STEP1,
  BOOKING_STEP2,
  BOOKING_STEP3,
} from 'core/redux/features/booking/constants';
import {
  selectActiveBookingStep,
  selectBookingStepData,
} from 'core/redux/features/booking/reducers';
import { BookingStep1, BookingStep2 } from 'core/redux/features/booking/types';
import { selectAllCategoryTags } from 'core/redux/features/filters/categories/reducers';
import { selectAllAreas } from 'core/redux/features/filters/reducers';
import {
  selectActiveParentCity,
  selectLocal,
} from 'core/redux/features/products/reducers/productTypes';
import { selectActiveProduct } from 'core/redux/features/products/reducers/products';
import { IProduct, IProductMenu } from 'core/redux/features/products/types';
import {
  validateEmail,
  validatePhone,
} from 'core/redux/features/shared/validations';
import { AppState } from 'core/redux/reducers';
import { useEffect, useRef, useState } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
import { useSelector } from 'react-redux';
import { ebAppEvent } from './ebappevent';
import { loadKlaviyoNotification } from './klaviyo-notif';
import { selectTranslator } from 'core/redux/features/languages/trans';

export const getLearnq = () => {
  return window._learnq || [];
};

export const klaviyoTrack = (data: any[]) => {
  getLearnq().push(data);
  if (process.env.NODE_ENV !== 'production') {
    console.log('klaviyo data', data);
  }
};

const createKlaviyoProductModel = (
  product: IProduct,
  allAreas,
  allTags,
  parentCity,
  activeLocal,
) => {
  const {
    name,
    id,
    price_sale,
    price_regular,
    primary_image,
    slug,
    product_type,
    tags,
    city_tags,
    city_id,
    parent_city_id,
  } = product;

  if (!id) {
    return;
  }

  const cityIds = [
    ...[...(city_tags || []), city_id, parent_city_id].map(id => {
      if (!id) {
        return false;
      }
      const find = allAreas.find(item => item.id === id);
      if (find) {
        return find.label;
      }

      return false;
    }),
    parentCity.label,
  ].filter((v, i, s) => v && s.indexOf(v) === i);
  const categoryTags = tags
    .map(item => {
      if (allTags.hasOwnProperty(item.id)) {
        return allTags[item.id].name;
      }

      return false;
    })
    .filter(Boolean);

  const domain = `${window.location.protocol}//${window.location.host}`;
  const localPath = activeLocal === 'en' ? 'en/' : '';
  let imageUrl = '';
  if (primary_image) {
    imageUrl = primary_image.startsWith('http')
      ? primary_image
      : `${domain}${primary_image}`;
  }

  return {
    ProductName: name,
    ProductID: id.toString(),
    Categories: [...cityIds, ...categoryTags],
    ImageURL: imageUrl,
    URL: `${domain}/${localPath}${product_type}/${slug}`,
    Brand: product_type,
    Price: price_sale,
    NormalPrice: price_regular,

    ParentCityName: parentCity.label,
    ParentCitySlug: parentCity.slug,
    ParentCityId: parentCity.id,
    ShortDescription: product.description,
  };
};

export const useGetProductForKlaviyo = () => {
  const activeLocal = useSelector(selectLocal);
  const product = useSelector(selectActiveProduct);
  const parentCity = useSelector(selectActiveParentCity);
  const allTags = useSelector(selectAllCategoryTags);
  const allAreas = useSelector(selectAllAreas);

  const [productModel, setProductModel] = useState<Record<any, any>>();
  useEffect(() => {
    const productModel = createKlaviyoProductModel(
      product,
      allAreas,
      allTags,
      parentCity,
      activeLocal,
    );

    setProductModel(productModel);
  }, [activeLocal, allAreas, allTags, parentCity, product]);

  return productModel;
};

export const useKlaviyoTrackCheckoutStarted = () => {
  const trans = useSelector(selectTranslator);
  const parentCity = useSelector(selectActiveParentCity);
  const activeLocal = useSelector(selectLocal);
  const product = useSelector(selectActiveProduct);
  const activeBookingStep = useSelector(selectActiveBookingStep);
  const bookingData = useSelector((state: AppState) =>
    selectBookingStepData(state, BOOKING_STEP1),
  ) as BookingStep1;

  const { data: guestData, complete: isStep2Complete } = useSelector(
    (state: AppState) => selectBookingStepData(state, BOOKING_STEP2),
  ) as BookingStep2;

  const order = useSelector((state: AppState) => {
    return state.booking.order;
  });

  const menus = useSelector(
    (state: AppState) => state.booking.menus.entities?.menus,
  );

  const lastTrackId = useRef(0);
  const productModel = useGetProductForKlaviyo();
  const isIdentified = useKlaviyoIdentify(
    guestData.email,
    guestData.phone,
    guestData.name,
    undefined,
    guestData.is_subscribe ? parentCity.label : undefined,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!isStep2Complete || activeBookingStep !== BOOKING_STEP3) {
      return;
    }

    const bookingMenus = bookingData.data.menus;
    const holdId = order.hold_id;

    if (
      isIdentified === -1 ||
      !holdId ||
      lastTrackId.current === holdId ||
      !productModel
    ) {
      return;
    }

    const Items = Object.values(bookingMenus).map(item => {
      const details = menus[item.menu_id] as IProductMenu;
      return {
        ProductID: productModel.ProductID,
        SKU: details.id,
        ProductName: details.name,

        ParentCityName: parentCity.label,
        ParentCitySlug: parentCity.slug,
        ParentCityId: parentCity.id,

        Quantity: item.num_person,
        ItemPrice: details.price_sale,
        RowTotal: details.price_sale * item.num_person,
        ProductURL: productModel.URL,
        ImageURL: productModel.ImageURL,
        ProductCategories: productModel.Categories,
      };
    });

    const bookPTypeUrl =
      product.product_type === 'restaurant'
        ? trans('booking_restaurant_slug')
        : product.product_type;
    const localPath = activeLocal === 'en' ? '/en/' : '';
    const domain = `${window.location.protocol}//${window.location.host}${localPath}`;
    const cartData = {
      $event_id: `${holdId}_${new Date().getTime()}`,
      $value: order.total,
      ItemNames: Object.values(bookingMenus).map(item => {
        return menus[item.menu_id].name;
      }),
      CheckoutURL: `${domain}/book/step1/${bookPTypeUrl}/${product.slug}`,
      Categories: productModel.Categories,
      RestaurantName: productModel.ProductName,
      Items: Items,

      ImageURL: productModel.ImageURL,
      NormalPrice: productModel.NormalPrice,
      SalePrice: productModel.Price,
      ShortDescription: productModel.ShortDescription,
    };

    klaviyoTrack(['track', 'Started Checkout', cartData]);
    lastTrackId.current = holdId;
  }, [
    activeBookingStep,
    productModel,
    activeLocal,
    isIdentified,
    bookingData,
    order,
    menus,
    parentCity.id,
    isStep2Complete,
  ]);
};

export const useKlaviyoTrackProductView = (product: IProduct) => {
  const activeLocal = useSelector(selectLocal);
  const parentCity = useSelector(selectActiveParentCity);
  const allTags = useSelector(selectAllCategoryTags);
  const allAreas = useSelector(selectAllAreas);

  //klaviyo
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!product) {
      return;
    }
    const item = createKlaviyoProductModel(
      product,
      allAreas,
      allTags,
      parentCity,
      activeLocal,
    );

    klaviyoTrack(['track', 'Viewed Product', item]);
    klaviyoTrack([
      'trackViewedItem',
      {
        Title: item.ProductName,
        ItemId: item.ProductID,
        Categories: item.Categories,
        ImageUrl: item.ImageURL,
        Url: item.URL,
        Metadata: {
          Brand: item.Brand,
          Price: item.Price,
          NormalPrice: item.NormalPrice,
          ShortDescription: item.ShortDescription,

          ParentCityName: parentCity.label,
          ParentCitySlug: parentCity.slug,
          ParentCityId: parentCity.id,
        },
      },
    ]);
  }, [product, activeLocal, parentCity.id, allTags, allAreas]);
};

export const useKlaviyoIdentify = (
  email: string,
  phone: string,
  fullName: string,
  source?: string,
  subscribeToCity?: string,
) => {
  const [isIdentified, setIsIdentified] = useState<number>(-1);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const payload = {};
    if (email !== null && email.length && validateEmail(email)) {
      payload['$email'] = email.replace(/\s+/g, '');
    }

    if (phone !== null && validatePhone(phone)) {
      payload['$phone_number'] = phone.replace(/\s+/g, '');
    }

    if (fullName !== null && fullName.length > 0) {
      const name = fullName.trim().split(' ', 2);
      if (name.length > 0) {
        payload['$first_name'] = name[0];
      }

      if (name.length === 2) {
        payload['$last_name'] = name[1];
      }
    }

    if (source) {
      payload['$source'] = source;
    }

    if (subscribeToCity && subscribeToCity.length > 0) {
      // noinspection JSNonASCIINames
      payload['Vælg by'] = subscribeToCity;
    }

    if (payload.hasOwnProperty('$email')) {
      klaviyoTrack(['identify', payload]);
      setIsIdentified(BOOKING_STEP2);

      // also send the data the app
      ebAppEvent('guest_email_added', { email });
      try {
        loadKlaviyoNotification({
          id: null,
          email: email,
        });
      } catch (e) {
        console.error(e);
      }
    }
  }, [email, phone, fullName, subscribeToCity]);

  useEffect(() => {
    // wait until klaviyo script is ready
    // then check if user is already identified
    const checker = setInterval(function () {
      if (getLearnq().hasOwnProperty('identify')) {
        const identity = getLearnq().identify();
        const isIdentified = identity.hasOwnProperty('$email');
        if (isIdentified) {
          setIsIdentified(BOOKING_STEP1);

          try {
            loadKlaviyoNotification({
              id: null,
              email: identity.$email,
            });
          } catch (e) {
            console.error(e);
          }
        }

        clearInterval(checker);
      }
    }, 1000);

    return () => {
      clearInterval(checker);
    };
  }, []);

  return isIdentified;
};
